<template>

  <v-card variant="elevated">
    <v-card-title>
      <v-container class="ml-n1">
        <v-row justify="start">
          <v-col class="w-75">
             SAML
             &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
             &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
            <span v-if="metadataExists" class="metadata">
              <a target="_blank" :href="metadata.metadataUrl">Download SAML Metadata</a>
            </span>
            <span v-else class="metadata">
              Download SAML Metadata
            </span>
          </v-col>
        </v-row>
      </v-container>
    </v-card-title>

    <v-alert v-model="statusSuccess" type="success" variant="tonal" dismissible class="mx-4 mb-4">
      {{ statusMessage }}
    </v-alert>
    <v-alert v-model="statusError" type="error" variant="tonal" dismissible class="mx-4 mb-4">
      {{ statusMessage }}
    </v-alert>

    <v-alert v-if="needsMigration" type="info" variant="tonal" class="mx-4 mb-4">
      <div class="mb-2">
        <strong>New SAML hostname available.</strong> This SAML configuration is still using the legacy hostname.
        Give the values below to your IdP admin and reconfigure your IdP to trust them <em>before</em> migrating,
        or SSO will stop working for your users until that's done.
      </div>
      <div><strong>Service Provider Entity ID:</strong> {{ metadata.newSpEntityId }}</div>
      <div><strong>Assertion Consumer Service URL:</strong> {{ metadata.newAcsUrl }}</div>
      <div><strong>Single Logout Service URL:</strong> {{ metadata.newSloUrl }}</div>
      <div class="mt-2">
        <button class="btn btn-primary" @click="showMigrateDialog = true">Migrate to new hostname</button>
      </div>
    </v-alert>

    <v-dialog v-model="showMigrateDialog" max-width="600px">
      <v-card>
        <v-card-title>Migrate to new hostname?</v-card-title>
        <v-card-text>
          <div class="mb-2">
            This will immediately change the SAML SP Entity ID / ACS URL / SLO URL to the new hostname:
          </div>
          <div><strong>Entity ID:</strong> {{ metadata.newSpEntityId }}</div>
          <div><strong>ACS URL:</strong> {{ metadata.newAcsUrl }}</div>
          <div><strong>SLO URL:</strong> {{ metadata.newSloUrl }}</div>
          <div class="mt-2">
            Make sure the IdP has already been reconfigured to trust these values, or SSO will break for
            your users until it is.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <button class="me-4 btn btn-default" @click="showMigrateDialog = false">Cancel</button>
          <button class="me-4 btn btn-primary" @click="migrateToNewHost">Continue</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showRevertDialog" max-width="600px">
      <v-card>
        <v-card-title>Revert to legacy hostname?</v-card-title>
        <v-card-text>
          <div class="mb-2">
            This will immediately change the SAML SP Entity ID / ACS URL / SLO URL back to the legacy hostname:
          </div>
          <div><strong>Entity ID:</strong> {{ metadata.legacySpEntityId }}</div>
          <div><strong>ACS URL:</strong> {{ metadata.legacyAcsUrl }}</div>
          <div><strong>SLO URL:</strong> {{ metadata.legacySloUrl }}</div>
          <div class="mt-2">
            Make sure the IdP has already been reconfigured to trust these values, or SSO will break for
            your users until it is.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <button class="me-4 btn btn-default" @click="showRevertDialog = false">Cancel</button>
          <button class="me-4 btn btn-primary" @click="revertToLegacyHost">Continue</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title>Delete SAML metadata?</v-card-title>
        <v-card-text>
          Do you wish to delete the SAML metadata?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <button class="me-4 btn btn-default" @click="showDeleteDialog = false">Cancel</button>
          <button class="me-4 btn btn-primary" @click="deleteMetadata">Delete</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-text>
      <v-form v-model="valid" ref="form" @submit.prevent>
      <v-container class="ml-n1">

        <v-row justify="start">
          <v-col class="w-75">
            <v-text-field label="Service Provider Entity ID" variant="outlined"
                          v-model="metadata.spEntityId" readonly
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-75">
            <v-text-field label="Assertion Consumer Service URL" variant="outlined"
                          v-model="metadata.acsUrl" readonly
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-75">
            <v-text-field label="Single Logout Service URL" variant="outlined"
                          v-model="metadata.sloUrl" readonly
            ></v-text-field>
            <div v-if="canRevert" class="text-caption text-medium-emphasis mt-n3 mb-2">
              <a href="#" @click.prevent="showRevertDialog = true">Accidentally migrated? Revert to legacy hostname</a>
            </div>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-75">
            <v-text-field label="Single Sign-On URL" variant="outlined"
                          v-model="metadata.loginUrl" :rules="rules.required"></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-75">
            <v-text-field label="Single Sign-Out URL (optional)" variant="outlined"
                          v-model="metadata.logoutUrl"></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-25">
            <v-text-field label="Issuer (IDP Entity ID)" variant="outlined"
                          v-model="metadata.entityId" :rules="rules.required"></v-text-field>
          </v-col>
          <v-col class="w-75">
            <v-select
                label="NameID policy format"
                v-model="metadata.nameIdFormat"
                :items="nameIdFormatItems"
                :rules="rules.required"
                item-text="k"
                item-value="v"
                class="w-75"
            ></v-select>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col class="w-25">
            <v-radio-group v-model="metadata.loginBinding" inline :rules="rules.required">
              <v-radio label="HTTP-POST" value="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"></v-radio>
              <v-radio label="HTTP-Redirect" value="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col class="w-25">
            <v-checkbox label="Sign SAML Request" v-model="metadata.signAuthnRequest"></v-checkbox>
          </v-col>
          <v-col class="w-50">
            <v-select
                label="Signature Algorithm"
                v-model="metadata.signingAlgorithm"
                :items="signingAlgorithmItems"
                :rules="rules.required"
                item-text="k"
                item-value="v"
                class="w-75"
            ></v-select>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col>
            <v-textarea label="Verification / Validation Certificate" variant="outlined"
                        v-model="metadata.verificationCert" :rules="rules.required"
                        class="cert-mono"
                        clearable clear-icon="mdi-close-circle"></v-textarea>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col>
            <v-text-field label="Admin Group Mapping" variant="outlined"
                          placeholder="vyopta_admin"
                          v-model="metadata.adminRoleMapping"></v-text-field>
          </v-col>
          <v-col>
            <v-text-field label="User Group Mapping" variant="outlined"
                          placeholder="vyopta_vanrptvwr"
                          v-model="metadata.userRoleMapping"></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col>
            <v-text-field label="Dashboard Group Mapping" variant="outlined"
                          placeholder="vyopta_vanDbVwr"
                          v-model="metadata.dashboardRoleMapping"></v-text-field>
          </v-col>
          <v-col>
            <v-text-field label="Read-only Group Mapping" variant="outlined"
                          placeholder="vyopta_vanRptRdr"
                          v-model="metadata.readOnlyRoleMapping"></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="start" v-if="showDomainAlias">
          <v-col class="w-25">
            <v-text-field label="Domain Aliases" variant="outlined"
                          v-model="metadata.domainAliases"></v-text-field>
          </v-col>
          <v-col class="w-25">
          </v-col>
        </v-row>

      </v-container>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <button class="me-4 btn btn-primary" @click="save" :disabled="!valid">Save</button>
      <button class="me-4 btn btn-default" @click="cancel">Cancel</button>
      <button class="me-4 btn btn-default" @click="showDeleteDialog = true">Delete</button>
    </v-card-actions>
  </v-card>

</template>
<style>
.cert-mono textarea {
  font-family: monospace;
}
.metadata {
  font-size: 14px;
}
</style>
<script>
import {mapGetters} from "vuex";
export default {
  data() {
    return {
      valid: false,
      showMigrateDialog: false,
      showRevertDialog: false,
      showDeleteDialog: false,
      statusSuccess: false,
      statusError: false,
      statusMessage: '',
      metadata: {
        exists: false,
        registrationId: '',
        loginUrl: '',
        loginBinding: '',
        logoutUrl: '',
        entityId: '',
        nameIdFormat: '',
        signAuthnRequest: false,
        signingAlgorithm: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256',
        verificationCert: '',
        adminRoleMapping: '',
        userRoleMapping: '',
        dashboardRoleMapping: '',
        readOnlyRoleMapping: '',
        acsUrl: '',
        sloUrl: '',
        metadataUrl: '',
        spEntityId: '',
        domainAliases: '',
        // live preview of what this org's SP identity would be on the new (golden) hostname - always computed by
        // the server, purely for display/comparison; never sent back except via the dedicated migrate action
        newSpEntityId: '',
        newAcsUrl: '',
        newSloUrl: '',
        newMetadataUrl: '',
        // live preview of what this org's SP identity would be if reverted back to the legacy (pre-rebrand)
        // hostname - always computed by the server, purely for display/comparison; never sent back except via
        // the dedicated revert action
        legacySpEntityId: '',
        legacyAcsUrl: '',
        legacySloUrl: '',
        legacyMetadataUrl: ''
      },
      nameIdFormatItems: [
        {k: 'Persistent', v: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent'},
        {k: 'Transient', v: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient'},
        {k: 'Email Address', v: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress'},
        {k: 'Unspecified', v: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified'}
      ],
      signingAlgorithmItems: [
        {k: 'RSA-SHA256', v: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256'},
        {k: 'RSA-SHA512', v: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha512'}
      ],
      // https://rmirabelle.medium.com/fixing-vuetifys-form-validation-3a5781ea43fe
      rules: {
      },
    };
  },
  methods: {
    // shows a dismissible success/error banner; success and error are mutually exclusive, and starting a new
    // action clears whatever banner was left over from the previous one
    showStatus(success, message) {
      this.statusSuccess = success;
      this.statusError = !success;
      this.statusMessage = message;
    },
    async refresh() {
      this.rules = {}
      this.loading = true;
      this.metadata = await this.$store.dispatch("singleSignOn/getSamlMetadata");
      this.loading = false;
    },
    async cancel() {
      await this.refresh();
    },
    async save() {
      this.statusSuccess = false;
      this.statusError = false;
      // replace empty rules with real rules
      this.rules = {
        required: [(value) => !!value || "Required."]
      }
      // let the DOM update
      await this.$nextTick();
      // manually trigger Vuetify validation
      if(this.$refs.form.validate()) {
        // form is valid, save metadata
        const success = await this.$store.dispatch("singleSignOn/createSamlMetadata", {metadata: this.metadata});
        await this.refresh();
        this.showStatus(success, success ? 'SAML metadata saved.' : 'Failed to save SAML metadata.');
      }
    },
    async deleteMetadata() {
      this.showDeleteDialog = false;
      this.statusSuccess = false;
      this.statusError = false;
      const success = await this.$store.dispatch('singleSignOn/deleteSamlMetadata');
      await this.refresh();
      this.showStatus(success, success ? 'SAML metadata deleted.' : 'Failed to delete SAML metadata.');
    },
    async migrateToNewHost() {
      this.showMigrateDialog = false;
      this.statusSuccess = false;
      this.statusError = false;
      // useNewSpHost is a write-only trigger - deliberately not part of the persistent `metadata` model, so a
      // routine field edit (e.g. group mappings) via the regular Save button can never accidentally trigger it
      const success = await this.$store.dispatch('singleSignOn/createSamlMetadata', {
        metadata: {...this.metadata, useNewSpHost: true}
      });
      await this.refresh();
      this.showStatus(success, success ? 'Migrated to new hostname.' : 'Failed to migrate to new hostname.');
    },
    async revertToLegacyHost() {
      this.showRevertDialog = false;
      this.statusSuccess = false;
      this.statusError = false;
      // useLegacySpHost is a write-only trigger - the symmetric "undo" for useNewSpHost above; deliberately not
      // part of the persistent `metadata` model, so a routine field edit can never accidentally trigger it
      const success = await this.$store.dispatch('singleSignOn/createSamlMetadata', {
        metadata: {...this.metadata, useLegacySpHost: true}
      });
      await this.refresh();
      this.showStatus(success, success ? 'Reverted to legacy hostname.' : 'Failed to revert to legacy hostname.');
    },
  },
  computed: {
    ...mapGetters("user", {
      orgContext: "getOrgContext",
      isSU: 'isSU'
    }),
    metadataExists: function() {
      return this.metadata.exists;
    },
    showDomainAlias: function() {
      return this.isSU;
    },
    // the server never reports "is this org migrated" directly (useNewSpHost is write-only) - derive it by
    // comparing the active, stored ACS URL against the live golden-hostname preview the server always computes
    needsMigration: function() {
      return this.metadataExists && !!this.metadata.newAcsUrl && this.metadata.acsUrl !== this.metadata.newAcsUrl;
    },
    // symmetric "undo" check: this org is currently on the new (golden) hostname, and the legacy hostname is a
    // genuinely different value in this environment - offer a way to back out of an accidental migration
    canRevert: function() {
      return this.metadataExists && !!this.metadata.legacyAcsUrl && this.metadata.acsUrl === this.metadata.newAcsUrl
        && this.metadata.acsUrl !== this.metadata.legacyAcsUrl;
    }
  },
  watch: {
    orgContext: function (val) {
      if (!val) {
        return;
      }
      this.refresh()
    }
  },
  async created() {
    await this.refresh();
  }
};
</script>
